<template>
    <div class="mx-8">
        <AppPageHeader>
            <template #title>Cotización </template>
            <template #subtitle
                >Análisis IA de solicitud de cotización</template
            >
            <template #headerRight>
                <AppButton
                    class="bg-gray-300"
                    @click="refreshData"
                    :disabled="loadingRequests"
                    :title="
                        loadingRequests ? 'Cargando...' : 'Refrescar listado'
                    "
                >
                    <svg
                        class="w-4 h-4 mr-2"
                        :class="{ 'animate-spin': loadingRequests }"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        ></path>
                    </svg>
                    {{ loadingRequests ? "Cargando..." : "Actualizar" }}
                </AppButton>
            </template>
        </AppPageHeader>

        <div class="mb-12">
            <!-- Tabla de cotizaciones -->
            <table
                class="table w-full border-collapse border-none rounded text-sm"
            >
                <thead class="rounded-t table-row-group">
                    <tr class="table-row">
                        <th
                            class="table-cell font-bold w-4/12 border-none text-neutral-600 text-left p-2"
                        >
                            Razón Social
                        </th>
                        <th
                            class="table-cell font-bold w-3/12 border-none text-neutral-600 text-left p-2"
                        >
                            Alcance Enviado
                        </th>
                        <th
                            class="table-cell font-bold w-2/12 border-none text-neutral-600 text-left p-2"
                        >
                            Análisis
                        </th>
                        <th
                            class="table-cell font-bold w-2/12 border-none text-neutral-600 text-left p-2"
                        >
                            Fecha Envío
                        </th>
                        <th
                            class="table-cell font-bold w-1/12 border-none text-neutral-600 text-center p-2"
                        >
                            Acciones
                        </th>
                    </tr>
                </thead>
                <tbody class="table-row-group" v-if="loadingRequests">
                    <tr
                        v-for="n in 3"
                        :key="n"
                        class="table-row border-b border-zinc-200"
                    >
                        <td class="table-cell p-2">
                            <div
                                class="bg-slate-300 w-full rounded h-6 animate-pulse"
                            ></div>
                        </td>
                        <td class="table-cell p-2">
                            <div
                                class="bg-slate-300 w-full rounded h-6 animate-pulse"
                            ></div>
                        </td>
                        <td class="table-cell p-2">
                            <div
                                class="bg-slate-300 w-2/3 rounded h-6 animate-pulse"
                            ></div>
                        </td>
                        <td class="table-cell p-2">
                            <div
                                class="bg-slate-300 w-3/4 rounded h-6 animate-pulse"
                            ></div>
                        </td>
                        <td class="table-cell p-2">
                            <div
                                class="bg-slate-300 w-8 h-8 rounded animate-pulse mx-auto"
                            ></div>
                        </td>
                    </tr>
                </tbody>
                <tbody class="table-row-group" v-else-if="errorMessage">
                    <tr class="table-row">
                        <td colspan="5" class="table-cell p-8 text-center">
                            <div class="text-red-600 mb-2">
                                {{ errorMessage }}
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody
                    class="table-row-group"
                    v-else-if="quotationRequests.length === 0"
                >
                    <tr class="table-row">
                        <td
                            colspan="5"
                            class="table-cell p-8 text-center text-gray-500"
                        >
                            No hay solicitudes de cotización para mostrar
                        </td>
                    </tr>
                </tbody>
                <tbody class="table-row-group" v-else>
                    <tr
                        v-for="request in quotationRequests"
                        :key="request.id"
                        class="table-row border-b border-zinc-200 hover:bg-gray-50 cursor-pointer align-top"
                    >
                        <td class="table-cell p-2 align-top">
                            <div
                                class="font-medium text-gray-900 mb-1 text-base"
                            >
                                {{ request.razon_social }}
                            </div>
                            <div class="flex flex-wrap gap-1">
                                <span
                                    v-for="norma in request.normas"
                                    :key="norma"
                                    class="inline-block text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded"
                                >
                                    {{ norma }}
                                </span>
                            </div>
                        </td>
                        <td class="table-cell p-2 align-top">
                            <div class="text-gray-700 text-xs leading-relaxed">
                                {{ request.alcance_actual }}
                            </div>
                        </td>
                        <td class="table-cell p-2 align-top">
                            <StatusBadge :status="request.status" />
                        </td>
                        <td class="table-cell p-2 align-top">
                            <div class="text-gray-900 text-sm font-medium">
                                {{ formatDateOnly(request.created_at) }}
                            </div>
                            <div class="text-gray-500 text-xs">
                                {{ formatTimeOnly(request.created_at) }}
                            </div>
                        </td>
                        <td class="table-cell p-2 text-center align-top">
                            <div class="flex gap-2 justify-center">
                                <button
                                    class="bg-gray-300 p-3 shadow-md"
                                    :disabled="loadingReExecution[request.id]"
                                    :title="getButtonTitle(request.status)"
                                    @click="reExecuteAnalysis(request)"
                                >
                                    <Loader2
                                        v-if="loadingReExecution[request.id]"
                                        class="w-5 h-5 text-slate-800 animate-spin"
                                    />
                                    <RotateCcw
                                        v-else
                                        class="w-5 h-5 text-slate-800"
                                    />
                                </button>
                                <button
                                    class="bg-gray-300 p-3 shadow-md"
                                    :disabled="
                                        loadingEmailSend[request.id] ||
                                        request.status !== 'completado'
                                    "
                                    :title="getEmailButtonTitle(request.status)"
                                    @click="sendAnalysisByEmail(request)"
                                >
                                    <Loader2
                                        v-if="loadingEmailSend[request.id]"
                                        class="w-5 h-5 text-slate-800 animate-spin"
                                    />
                                    <Send
                                        v-else
                                        class="w-5 h-5 text-slate-800"
                                    />
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { ref, onMounted } from "vue";
import { RotateCcw, Loader2, Send } from "lucide-vue-next";
import AppPageHeader from "../components/AppPageHeader.vue";
import AppButton from "../components/AppButton.vue";
import StatusBadge from "../components/StatusBadge.vue";

interface QuotationRequest {
    id: string;
    razon_social: string;
    normas: string[];
    alcance_actual: string;
    status: "pendiente" | "en_proceso" | "completado";
    created_at: string;
}

const quotationRequests = ref<QuotationRequest[]>([]);
const loadingRequests = ref(true);
const errorMessage = ref("");
const loadingReExecution = ref<Record<string, boolean>>({});
const loadingEmailSend = ref<Record<string, boolean>>({});

const loadQuotationRequests = async () => {
    try {
        loadingRequests.value = true;
        errorMessage.value = "";
        const response = await axios.get("/v2/ai/quotation-requests");

        quotationRequests.value = response.data;
    } catch (error) {
        console.error("Error loading quotation requests:", error);
        errorMessage.value = "Error al cargar las solicitudes de cotización";
    } finally {
        loadingRequests.value = false;
    }
};

const refreshData = async () => {
    await loadQuotationRequests();
};

const reExecuteAnalysis = async (request: QuotationRequest) => {
    try {
        loadingReExecution.value[request.id] = true;

        const response = await axios.post(
            `/v2/ai/quotation-requests/${request.id}/reexecute`
        );

        if (response.data.success) {
            // Actualizar el status en la lista local
            const index = quotationRequests.value.findIndex(
                (q) => q.id === request.id
            );
            if (index !== -1) {
                quotationRequests.value[index].status =
                    response.data.status || "en_proceso";
            }

            // Mostrar mensaje de éxito
            alert("Análisis IA iniciado correctamente");
        }
    } catch (error) {
        console.error("Error re-executing analysis:", error);
        alert("Error al re-ejecutar el análisis. Inténtalo de nuevo.");
    } finally {
        loadingReExecution.value[request.id] = false;
    }
};

const getButtonTitle = (status: string) => {
    const titles = {
        pendiente: "Ejecutar análisis IA",
        en_proceso: "Re-ejecutar análisis IA",
        completado: "Re-ejecutar análisis IA",
    };
    return titles[status as keyof typeof titles] || "Ejecutar análisis IA";
};

const getEmailButtonTitle = (status: string) => {
    if (status === "completado") {
        return "Enviar análisis por email";
    }
    return "Análisis debe estar completado para enviar por email";
};

const sendAnalysisByEmail = async (request: QuotationRequest) => {
    try {
        loadingEmailSend.value[request.id] = true;

        const response = await axios.post(
            `/v2/ai/quotation-requests/${request.id}/send-email`
        );

        if (response.data.success) {
            alert("Análisis enviado por email correctamente");
        }
    } catch (error) {
        console.error("Error sending analysis by email:", error);
        alert("Error al enviar el análisis por email. Inténtalo de nuevo.");
    } finally {
        loadingEmailSend.value[request.id] = false;
    }
};

const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDateOnly = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const formatTimeOnly = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

onMounted(() => {
    loadQuotationRequests();
});
</script>
